/* eslint class-methods-use-this: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import SessionStore from 'stores/SessionStore';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { getCurrentDeliveryOrder, getDocKey, isPDF } from 'utils/documents';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import LoadingCircle from 'components/ui/LoadingCircle';
import {
  getDocuments, getDocumentTypes,
  getSignedUrl,
  uploadDocuments,
} from 'utils/DocumentsAPIUtils';
import {
  carrierDocTypes,
  carrierViewableDocTypesMap,
  CUSTOM,
  shipmentDocTypes,
  shipmentDocTypesMap,
  shipperDocTypes,
  shipperViewableDocTypesMap,
  carrierPortalDocTypes,
} from 'config/documents';
import AddButton from 'components/v2/buttons/AddButton';
import PDFViewer from 'components/modules/shipments/PDFViewer';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import DocumentIcon from 'components/v2/icons/DocumentIcon';
import { showToast } from 'stores/ToastStore';
import AdminServerActionCreators from 'actions/AdminServerActionCreators';

const openSignedUrl = (key) => async (event) => {
  event.preventDefault();
  try {
    const { data: signedUrl } = await getSignedUrl(key);

    const url = signedUrl?.getSignedUrl?.signedUrl;

    window.open(url, 'new');
  } catch (e) {
    console.error('ShipmentListDocuments: error getting signed url: ', e);
  }
};

class ShipmentListDocuments extends React.Component {
  static propTypes = {
    shipment: PropTypes.object,
  };
  static defaultProps = {
    shipment: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      showUploadModal: false,
      userRole: null,
      documents: [],
      isLoading: true,
    };
  }

  componentDidMount() {
    const user = SessionStore.getUser();
    this.setState({ userRole: user._type });

    if (this.props.shipment?._id) {
      this.initDocuments();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.shipment?._id !== prevProps.shipment?._id) {
      this.initDocuments();
    }
  }

  showGetDocumentsErrorToast = (err) => {
    const { t } = this.props;
    showToast(
      t('GET_DOCUMENT_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  async initDocuments() {
    this.setState({ isLoading: true });

    const { data, errors } = await getDocuments(
      [this.props.shipment._id],
      undefined,
    );

    if (errors) {
      this.showGetDocumentsErrorToast(errors[0].message);
      return;
    }

    const documents = data?.getDocuments?.documents;

    this.setState({ documents, isLoading: false });
  }

  renderDocuments() {
    const { t } = this.props;

    if (this.state.isLoading) {
      return <LoadingCircle text={t('LOADING')} />;
    }

    if (!this.state.documents.length) {
      return <span>{t('NO_DOCS_ATTACHED')}</span>;
    }

    const { userRole } = this.state;
    const scopedDocs = this.state.documents.filter((doc) => {
      if (userRole === 'Admin') {
        return true;
      }
      const docType = getDocKey(doc.type);

      if (userRole === 'Shipper') {
        return !!shipperViewableDocTypesMap[docType];
      }

      return !!carrierViewableDocTypesMap[docType];
    });

    return this.keepLatestDeliveryOrder(scopedDocs).map((doc, index) => {
      const tooltip = <Tooltip id={doc._id}>{t(sluggify(doc.name))}</Tooltip>;
      return (
        <div className="px-1">
          <a href={doc.key} onClick={openSignedUrl(doc.key)} key={index}>
            <OverlayTrigger
              placement="top"
              overlay={tooltip}
              style={{ display: 'inline-block' }}
            >
              <div>
                <DocumentIcon className="w-4 h-4 mb-1" />
              </div>
            </OverlayTrigger>
          </a>
        </div>
      );
    });
  }

  keepLatestDeliveryOrder = (docs) => {
    const deliveryOrder = getCurrentDeliveryOrder(docs);
    if (deliveryOrder) {
      return docs
        .filter((doc) => doc.key !== 'delivery_order')
        .concat([deliveryOrder]);
    }
    return docs;
  };

  renderUploadButton() {
    const { t } = this.props;
    const id = `upload-document-${this.props.shipment._id}`;
    const addDocumentTooltip = <Tooltip id={id}>{t('ADD_DOCUMENT')}</Tooltip>;
    return (
      <OverlayTrigger
        placement="top"
        overlay={addDocumentTooltip}
        style={{ display: 'inline-block' }}
      >
        <AddButton size="small" onClick={() => this.showUploadModal()} />
      </OverlayTrigger>
    );
  }

  showUploadModal = () => {
    this.setState({ showUploadModal: true });
  };

  hideUploadModal = () => {
    this.setState({ showUploadModal: false });
  };

  onUpload = () => {
    this.initDocuments();
  };

  render() {
    const { t } = this.props;
    return (
      <div>
        <div className="flex flex-wrap items-center -mx-1">
          {this.renderDocuments()}
        </div>
        {this.renderUploadButton()}
        {this.state.showUploadModal && (
          <DocumentUploadModal
            show={this.state.showUploadModal}
            onHide={this.hideUploadModal}
            onUpload={this.onUpload}
            user={this.state.userRole}
            shipment={this.props.shipment}
            t={t}
          />
        )}
      </div>
    );
  }
}

const CustomDocumentField = withTranslation()(
  class CustomDocumentField extends Component {
    constructor(props) {
      super(props);
      this.state = { title: '' };
    }

    render() {
      const { t } = this.props;
      return (
        <div>
          <label htmlFor="customTitle" className="form-label mb-1">
            {t('CUSTOM_TITLE')}
          </label>
          <input
            type="text"
            className="form-field"
            placeholder={t('ENTER_DOCUMENT_TITLE')}
            onChange={(e) => {
              this.setState({ title: e.target.value }, () => {
                this.props.onChange(this.state.title);
              });
            }}
          />
        </div>
      );
    }
  },
);

/* DOCUMENT UPLOAD MODAL */

/* Modal With Dropzone and File Previews for Document Uploads */
export class DocumentUploadModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      type: '',
      files: [],
      customTitle: '',
      types: [],
    };
  }

  componentDidMount() {
    // commenting out to bring back when we reimplement doc service docTypes API, see function below.
    // this.fetchDocumentTypes();
    this.setState({ types: carrierPortalDocTypes })
  }

  // Leaving until phase two we can reinstate
  fetchDocumentTypes = async () => {
    try {
      const {
        data,
        errors,
      } = await getDocumentTypes('shipment');

      if (errors && errors.length) {
        this.showGetDocumentTypesErrorToast(errors.join('\n'));
      }

      if (data) {
        const { getAvailableDocumentTypes } = data;
        const filteredTypes = getAvailableDocumentTypes.filter((type) =>
          Boolean(type.canUpload),
        );

        this.setState({ types: filteredTypes });
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  showGetDocumentTypesErrorToast = (error) => {
    const { t } = this.props;

    showToast(
      t('GET_DOCUMENT_TYPES_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error }),
      'error',
    );
  };

  handleDocumentTypeSelect = (item) => {
    const { value: type } = item;
    if (type !== CUSTOM.value) {
      this.setState({ customTitle: '' });
    }

    this.setState({ type });
  };

  renderSelection() {
    const { t } = this.props;
    const { types } = this.state;
    const { type } = this.state;

    if (types?.length) {
      return (
        <CargomaticSelect
          label={t('DOCUMENT_TYPE')}
          items={types}
          onSelect={this.handleDocumentTypeSelect}
          caption={t('PLEASE_SELECT_A_DOCUMENT_TYPE')}
          valueKey="value"
          textKey="localizationSlug"
          selected={type}
          showCaption
        />
      );
    } else {
      return (
        <div className="flex justify-start">
          <LoadingCircle />
          {t('GETTING_DOCUMENT_TYPES')}...
        </div>
      );
    }
  }

  handleCustomTitleChange = (title) => {
    this.setState({ customTitle: title });
  };

  renderPreview() {
    if (!this.state.files.length) {
      return null;
    }
    const filePreviews = this.state.files.map((file, index) => {
      const fileType = file.type;
      if (isPDF(fileType)) {
        return (
          <div key={index}>
            <PDFViewer file={file.preview} />
          </div>
        );
      }
      return (
        <div key={index}>
          <img width="100%" height="100%" src={file.preview} alt="Preview" />
        </div>
      );
    });

    return (
      <div>
        {filePreviews}
        <hr />
      </div>
    );
  }

  upload = async () => {
    this.props.onHide();

    try {
      const { t } = this.props;
      const { types, type } = this.state;
      const documentName =
        this.state.type === CUSTOM.value
          ? this.state.customTitle
          : this.state.type;

      const documentType = types.find((typeItem) => typeItem.value === type);

      const data = this.state.files.map((file) => {
        return {
          type: documentName,
          content: file,
          contentType: file.type,
          name: documentType?.name,
          size: file.size,
          foreignKeys: [this.props.shipment._id],
        };
      });

      const { error: uploadError } = await uploadDocuments(data);

      if (uploadError) {
        const standardErrorSlug = t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', {
          uploadError,
        });
        const limitErrorSlug = t('FILE_SIZE_TOO_BIG', {
          MB: uploadError._limit,
        });
        const slugError = uploadError._limit
          ? limitErrorSlug
          : standardErrorSlug;

        showToast(t('DOCUMENT_UPLOAD_FAILED_RETRY'), slugError, 'error');
      } else {
        AdminServerActionCreators.receiveShipmentUpdate(this.props.shipment);

        this.props.onUpload();
      }
    } catch (e) {
      console.error(e);
    }
  };

  // This looks weird since there's some race condition with dropzone and image previews
  handleFile = (file) => {
    this.setState((previousState) => {
      return {
        files: [...previousState.files, ...file],
      };
    });
  };

  renderUploader() {
    const { t } = this.props;
    return (
      <div>
        <div>{this.state.files.length ? this.renderPreview() : null}</div>
        <Dropzone
          style={{
            width: '100%',
            height: 100,
            padding: 10,
            borderWidth: 2,
            borderColor: '#337ab7',
            borderStyle: 'dashed',
            borderRadius: 5,
          }}
          onDrop={this.handleFile}
          multiple
          accept={ACCEPTED_FILE_TYPES}
        >
          <div>{t('CLICK_OR_DRAG_TO_UPLOAD_FILE')}</div>
        </Dropzone>
      </div>
    );
  }

  saveDisabled() {
    if (!this.state.files.length) {
      return true;
    }

    if (!this.state.type) {
      return true;
    }

    if (this.state.type === CUSTOM.value && !this.state.customTitle) {
      return true;
    }

    return false;
  }

  render() {
    const { t } = this.props;
    return (
      <Modal show={this.props.show} onHide={this.props.onHide}>
        <Modal.Header>
          <Modal.Title>
            {t('UPLOAD_ADDITIONAL_DOCUMENTS_SHIPMENT', {
              shipmentId: this.props.shipment.shipment_id,
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-hidden">{this.renderUploader()}</div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {this.renderSelection()}
            {this.state.type === CUSTOM.value && (
              <CargomaticInput
                label={t('CUSTOM_TITLE')}
                placeholder={t('ENTER_DOCUMENT_TITLE')}
                onChange={this.handleCustomTitleChange}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end">
            <button
              className="button button-green carrier-admin-attachDocument heap"
              onClick={this.upload}
              disabled={this.saveDisabled()}
            >
              {titleize(t('UPLOAD'))}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withTranslation()(ShipmentListDocuments);
